<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import {
  mcqChapterClusters,
  top100Mcqs,
} from "~/features/computer/data/mcq-clusters";
import { buildComputerNamedItemList } from "~/features/computer/data/seo/computer-schema";

const topStart = mcqChapterClusters.filter((item) =>
  ["critical", "high"].includes(item.priority),
);

const groupedTop100 = mcqChapterClusters.map((cluster) => ({
  ...cluster,
  questions: top100Mcqs.filter((question) => question.unit === cluster.unit),
}));

const summaryCards = [
  { title: "Top MCQs", value: top100Mcqs.length, detail: "Most searched objective set" },
  { title: "Critical", value: "Ch 2", detail: "Python sab se pehle" },
  { title: "High", value: "Ch 1, 3, 4", detail: "Paper coverage strong hoti hai" },
];

const relatedPrep = [
  { title: "Pairing Scheme", to: "/11th/computer/pairing-scheme/" },
  { title: "Paper Pattern", to: "/11th/computer/paper-pattern/" },
  { title: "Important Questions", to: "/11th/computer/important-questions/" },
  { title: "Practice Mode", to: "/11th/computer/practice/mcqs/" },
];
const faqs = [
  {
    question: "11th class computer top 100 MCQs kya hain?",
    answer: "Yeh selected objective questions ka set hai jo important chapters se liya gaya hai taake student fast revision kar sake.",
  },
  {
    question: "Top 100 MCQs me kin chapters par focus karna chahiye?",
    answer: "Chapter 2 Python, Chapter 1 SDLC, Chapter 3 Algorithms aur Chapter 4 Data Structures se start karna best rehta hai.",
  },
  {
    question: "Kya top 100 MCQs paper prep ke liye enough hain?",
    answer: "Top 100 MCQs fast objective prep ke liye useful hain, lekin full preparation ke liye chapter-wise MCQs aur pairing scheme bhi dekhni chahiye.",
  },
];

usePageSeo({
  title: "11th Class Computer MCQs Top 100 (Punjab Board) | Ustaad",
  description:
    "11th class computer MCQs top 100 for Punjab Board with chapter-wise objective questions, chapter cluster pages and fast revision links.",
  keywords:
    "11th class computer mcqs top 100, first year computer mcqs, chapter wise computer mcqs, punjab board computer mcqs",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "MCQs", item: "/11th/computer/mcqs/" },
    { name: "Top 100", item: "/11th/computer/mcqs/top-100/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: {
    about: {
      "@type": "Thing",
      name: "11th Class Computer Top 100 MCQs",
    },
    mainEntity: buildComputerNamedItemList(top100Mcqs.map((item) => item.question)),
  },
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Primary Traffic Page</p>
      <h1 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">
        11th Class Computer MCQs Top 100
      </h1>
      <p class="mt-4 max-w-3xl text-sm leading-7 text-slate-700 dark:text-slate-300">
        Yeh top-100 MCQs page objective prep ka fast entry point hai. Chapter 2, 1, 3 aur 4 se start karo, phir remaining chapters ko chapter-cluster pages se cover karo.
      </p>

      <div class="mt-6 flex flex-wrap gap-3">
        <a
          href="#top-100-list"
          class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-700"
        >
          View Top 100
        </a>
        <NuxtLink
          to="/11th/computer/practice/mcqs/"
          class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-600 dark:hover:text-sky-300"
        >
          Practice Mode
        </NuxtLink>
      </div>
    </section>

    <section class="grid gap-4 md:grid-cols-3">
      <article
        v-for="item in summaryCards"
        :key="item.title"
        class="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
      >
        <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">{{ item.title }}</p>
        <h2 class="mt-3 text-4xl font-bold tracking-tight text-slate-950 dark:text-white">{{ item.value }}</h2>
        <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.detail }}</p>
      </article>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Chapter Clusters</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Start with these chapters</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <NuxtLink
          v-for="item in topStart"
          :key="item.slug"
          :to="item.clusterTo"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Chapter {{ item.chapter }}</p>
          <h3 class="mt-2 text-lg font-semibold text-slate-950 dark:text-white">{{ item.shortTitle }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section id="top-100-list" class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Top 100</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Chapter-wise Top 100 MCQs</h2>
        </div>
        <p class="text-sm font-medium text-slate-500 dark:text-slate-400">Scan fast, revise faster</p>
      </div>

      <div class="mt-5 grid gap-4 lg:grid-cols-2">
        <article
          v-for="group in groupedTop100"
          :key="group.slug"
          class="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Chapter {{ group.chapter }}</p>
              <h3 class="mt-2 text-xl font-semibold text-slate-950 dark:text-white">{{ group.shortTitle }}</h3>
            </div>
            <span class="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              {{ group.questions.length }} MCQs
            </span>
          </div>

          <ol class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <li v-for="question in group.questions" :key="`${question.unit}-${question.id}`">
              {{ question.question }}
            </li>
          </ol>

          <div class="mt-5 flex flex-wrap gap-3">
            <NuxtLink
              :to="group.clusterTo"
              class="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
            >
              Chapter Page
            </NuxtLink>
            <NuxtLink
              :to="group.mcqTo"
              class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:border-sky-300 hover:text-sky-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-600 dark:hover:text-sky-300"
            >
              Full MCQs
            </NuxtLink>
          </div>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">All Chapters</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Chapter-wise MCQ Pages</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <NuxtLink
          v-for="item in mcqChapterClusters"
          :key="item.slug"
          :to="item.clusterTo"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Chapter {{ item.chapter }}</p>
          <h3 class="mt-2 text-lg font-semibold text-slate-950 dark:text-white">{{ item.shortTitle }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-slate-950 p-5 text-white md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">Related Prep</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight">Use MCQs with pattern and pairing</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <NuxtLink
          v-for="item in relatedPrep"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-white/10 bg-white/5 p-5 transition hover:bg-white/10"
        >
          <h3 class="text-lg font-semibold text-white">{{ item.title }}</h3>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Top 100 MCQs FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>
  </PageContainer>
</template>
